import { Mark } from './Mark';
import './logo.css';

/**
 * The product lockup: the official HLSC mark plus the product name, with the
 * company line underneath where there is room for it.
 *
 * The mark itself is unaltered official artwork (see Mark.tsx) — "Hanseatic
 * Select" and "Hanseatic LifeScience Consulting" are set here as plain Lato
 * text next to it, never merged into or presented as part of the mark. The
 * product name is legitimate to show (it is what this application is
 * called) but it is not part of the official logo, so it stays visually and
 * structurally separate: its own text node, its own styling, easy to tell
 * apart from the image beside it.
 */

export type LogoProps = {
  size?: 'sm' | 'md' | 'lg';
  /** Mark only, for collapsed navigation and small surfaces. */
  markOnly?: boolean;
  /** Plays the mark's entrance reveal once on mount — see Mark.tsx. */
  animate?: boolean;
  /** Historically a separate looping glow effect on the mark; that effect
   * used a filter/drop-shadow the brand guidelines forbid outright, so it
   * is gone. Kept as an accepted prop, now simply an alias for `animate`,
   * so the marketing site's existing header call site (signed off, not
   * owned here) plays the same one-shot reveal without needing to change
   * which props it passes. */
  wave?: boolean;
  /** Light text for dark/brand backgrounds. */
  inverse?: boolean;
  className?: string;
  /**
   * Override the mark's rendered pixel size without changing the text preset
   * `size` still controls (product-name size, whether the company line
   * shows). For a lockup that has the width for a bigger mark but still
   * wants the compact `sm` wordmark — see the app sidebar, which is exactly
   * that case.
   */
  markSize?: number;
  /**
   * Show the company line even at `sm`, which otherwise hides it to stay
   * compact. The lockup is mark plus product name plus company name — the
   * company line is what identifies the legal entity — so a caller with the
   * room for it (the app sidebar) should ask for it back rather than lose
   * it by default.
   */
  showCompany?: boolean;
};

const MARK_SIZE: Record<NonNullable<LogoProps['size']>, number> = {
  sm: 34,
  md: 46,
  lg: 68,
};

export function Logo({ size = 'md', markOnly = false, animate = false, wave = false, inverse = false, className, markSize, showCompany = false }: LogoProps) {
  return (
    <span
      className={[
        'logo',
        `logo-${size}`,
        inverse ? 'logo-inverse' : '',
        showCompany ? 'logo-with-company' : '',
        markOnly ? 'logo-mark-only' : '',
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Mark size={markSize ?? MARK_SIZE[size]} animate={animate || wave} title="Hanseatic LifeScience Consulting" />
      {!markOnly && (
        <span className="logo-words">
          <span className="logo-product">
            Hanseatic <span className="logo-product-accent">Select</span>
          </span>
          <span className="logo-company">Hanseatic LifeScience Consulting</span>
        </span>
      )}
    </span>
  );
}
